import React, { useEffect, useState } from 'react';
import {
  Plus,
  Save,
  Trash2,
  ArrowUp,
  ArrowDown,
  Loader2,
  CheckCircle2
} from 'lucide-react';
import { api } from '../../lib/api';
import ImageInput from '../../components/admin/ImageInput';
import { Field, TextInput, NumberInput, Toggle, Button } from '../../components/admin/FormControls';

const AdminSlides = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [msg, setMsg] = useState('');

  const load = async () => {
    setLoading(true);
    const { data } = await api.get('/slides');
    setSlides(data);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const flash = (m) => {
    setMsg(m);
    setTimeout(() => setMsg(''), 2200);
  };

  const update = (id, patch) =>
    setSlides((arr) => arr.map((s) => (s.id === id ? { ...s, ...patch } : s)));

  const save = async (slide) => {
    setSaving(slide.id);
    try {
      await api.put(`/slides/${slide.id}`, {
        image_url: slide.image_url,
        title: slide.title,
        subtitle: slide.subtitle,
        order: slide.order,
        active: slide.active
      });
      flash('Slide saved');
    } catch (e) {
      flash(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(null);
    }
  };

  const remove = async (slide) => {
    if (!window.confirm(`Delete slide "${slide.title}"?`)) return;
    await api.delete(`/slides/${slide.id}`);
    flash('Slide deleted');
    load();
  };

  const addNew = async () => {
    const nextOrder = (slides[slides.length - 1]?.order || 0) + 1;
    const { data } = await api.post('/slides', {
      image_url: '',
      title: 'New slide',
      subtitle: '',
      order: nextOrder,
      active: true
    });
    setSlides([...slides, data]);
  };

  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= slides.length) return;
    const next = [...slides];
    [next[idx], next[j]] = [next[j], next[idx]];
    // recompute orders 1..N
    const reordered = next.map((s, i) => ({ ...s, order: i + 1 }));
    setSlides(reordered);
    await api.post('/slides/reorder', {
      items: reordered.map((s) => ({ id: s.id, order: s.order }))
    });
    flash('Order updated');
  };

  return (
    <div>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#0B2545]">Hero Slides</h1>
          <p className="mt-1 text-[13.5px] text-[#0B2545]/65">
            Manage the home page slider. Use Order (1, 2, 3, 4...) or arrows to set position.
          </p>
        </div>
        <Button variant="gold" onClick={addNew}>
          <Plus size={14} /> Add slide
        </Button>
      </div>

      {msg && (
        <div className="mb-4 inline-flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-[13px] text-green-700">
          <CheckCircle2 size={14} /> {msg}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-[#0B2545]/60">
          <Loader2 size={16} className="animate-spin" /> Loading...
        </div>
      ) : slides.length === 0 ? (
        <div className="rounded-lg border border-[#E6DDC8] bg-white p-10 text-center text-[#0B2545]/60">
          No slides yet. Click "Add slide" to create one.
        </div>
      ) : (
        <div className="space-y-5">
          {slides.map((s, idx) => (
            <div
              key={s.id}
              className="rounded-lg border border-[#E6DDC8] bg-white p-5 shadow-sm"
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="inline-flex items-center gap-2">
                  <div className="flex h-8 w-8 items-center justify-center rounded-md bg-[#0B2545] font-serif text-sm font-bold text-white">
                    {s.order}
                  </div>
                  <span className="text-[12.5px] text-[#0B2545]/65">Position</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" onClick={() => move(idx, -1)} disabled={idx === 0}>
                    <ArrowUp size={13} />
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => move(idx, +1)}
                    disabled={idx === slides.length - 1}
                  >
                    <ArrowDown size={13} />
                  </Button>
                  <Button variant="danger" onClick={() => remove(s)}>
                    <Trash2 size={13} /> Delete
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
                <ImageInput
                  label="Slide image"
                  value={s.image_url}
                  onChange={(v) => update(s.id, { image_url: v })}
                  height={220}
                />
                <div className="space-y-4">
                  <Field label="Title">
                    <TextInput
                      value={s.title}
                      onChange={(v) => update(s.id, { title: v })}
                    />
                  </Field>
                  <Field label="Subtitle">
                    <TextInput
                      value={s.subtitle}
                      onChange={(v) => update(s.id, { subtitle: v })}
                    />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Order (position)">
                      <NumberInput
                        value={s.order}
                        onChange={(v) => update(s.id, { order: v })}
                        min={1}
                      />
                    </Field>
                    <Field label="Visibility">
                      <Toggle
                        checked={s.active}
                        onChange={(v) => update(s.id, { active: v })}
                        label={s.active ? 'Active' : 'Hidden'}
                      />
                    </Field>
                  </div>
                  <div className="pt-2">
                    <Button onClick={() => save(s)} disabled={saving === s.id}>
                      {saving === s.id ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <Save size={13} />
                      )}
                      Save changes
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminSlides;
